import { useEffect } from 'react';
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  FALLBACK_OG_IMAGE,
  TWITTER_HANDLE,
  buildCanonical,
} from '../config/seo';

/**
 * useSEO — declarative SEO head-tag manager for Cultiv AI Studio.
 *
 * Every field is optional; sensible project-wide defaults kick in
 * (canonical URL from window.location, default OG image, robots "index, follow").
 *
 * @param {Object}  opts
 * @param {string}  opts.title           Page <title> and og:title / twitter:title
 * @param {string}  opts.description     meta description + og/twitter description
 * @param {string}  [opts.ogImage]       Absolute or relative OG/Twitter image URL
 * @param {string}  [opts.canonical]     Full canonical URL (defaults to current pathname)
 * @param {string}  [opts.keywords]      meta keywords (comma-separated)
 * @param {string}  [opts.ogType]        og:type, defaults to "website"
 * @param {boolean} [opts.noindex]       If true, emits <meta name="robots" content="noindex, nofollow">
 * @param {Object}  [opts.jsonLd]        JSON-LD object to inject as <script type="application/ld+json">
 */
const useSEO = ({
  title,
  description,
  ogImage,
  canonical,
  keywords,
  ogType = 'website',
  noindex = false,
  jsonLd,
} = {}) => {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const fullTitle = title || SITE_NAME;
    document.title = fullTitle;

    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
    const canonicalUrl = canonical || buildCanonical(pathname);

    // Resolve OG image: absolute URL || prefix with SITE_URL || default
    let resolvedOgImage = DEFAULT_OG_IMAGE;
    if (ogImage) {
      resolvedOgImage = /^https?:\/\//i.test(ogImage) ? ogImage : `${SITE_URL}${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;
    }

    const setMeta = (name, content, property = false) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    const setLink = (rel, href) => {
      if (!href) return;
      let tag = document.querySelector(`link[rel="${rel}"]`);
      if (!tag) {
        tag = document.createElement('link');
        tag.setAttribute('rel', rel);
        document.head.appendChild(tag);
      }
      tag.setAttribute('href', href);
    };

    // Core
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical + og:url
    setLink('canonical', canonicalUrl);

    // Open Graph
    setMeta('og:site_name', SITE_NAME, true);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    setMeta('og:image', resolvedOgImage, true);
    setMeta('og:image:alt', fullTitle, true);

    // Twitter
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', TWITTER_HANDLE);
    setMeta('twitter:creator', TWITTER_HANDLE);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', resolvedOgImage);
    setMeta('twitter:image:alt', fullTitle);

    // JSON-LD structured data (one node per page)
    const JSONLD_ID = 'seo-jsonld';
    const existing = document.getElementById(JSONLD_ID);
    if (jsonLd) {
      const script = existing || document.createElement('script');
      script.type = 'application/ld+json';
      script.id = JSONLD_ID;
      script.textContent = JSON.stringify(jsonLd);
      if (!existing) document.head.appendChild(script);
    } else if (existing) {
      existing.remove();
    }

    return () => {
      // Reset title on unmount; leave canonical/meta in place so the next
      // page's useSEO call overwrites atomically (prevents flicker).
      document.title = SITE_NAME;
    };
  }, [title, description, ogImage, canonical, keywords, ogType, noindex, jsonLd]);
};

export { FALLBACK_OG_IMAGE };
export default useSEO;
